import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import type { AuditLog } from '@/types/audit';
import { formatFieldName, formatActionType } from './format-utils';

interface PDFExportOptions {
  title: string;
  data: AuditLog[];
  visibleColumns: string[];
  filters?: {
    assetId?: string;
    startDate?: string;
    endDate?: string;
    actionType?: string;
    user?: string;
  };
}

export function exportAuditLogsToPDF(options: PDFExportOptions): jsPDF {
  const { title, data, visibleColumns, filters } = options;
  
  // Create new PDF document
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  // Add title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 15);

  // Add generation date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`, 14, 22);

  // Add filters if any
  let yPosition = 28;
  if (filters && Object.keys(filters).some(key => filters[key as keyof typeof filters])) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Filters Applied:', 14, yPosition);
    yPosition += 5;
    
    doc.setFont('helvetica', 'normal');
    if (filters.assetId) {
      doc.text(`Asset ID: ${filters.assetId}`, 14, yPosition);
      yPosition += 5;
    }
    if (filters.startDate) {
      doc.text(`Start Date: ${format(new Date(filters.startDate), 'dd/MM/yyyy')}`, 14, yPosition);
      yPosition += 5;
    }
    if (filters.endDate) {
      doc.text(`End Date: ${format(new Date(filters.endDate), 'dd/MM/yyyy')}`, 14, yPosition);
      yPosition += 5;
    }
    if (filters.actionType) {
      doc.text(`Action Type: ${formatActionType(filters.actionType)}`, 14, yPosition);
      yPosition += 5;
    }
    if (filters.user) {
      doc.text(`User: ${filters.user}`, 14, yPosition);
      yPosition += 5;
    }
    yPosition += 3;
  }

  // Prepare table headers based on visible columns
  const headers: string[] = [];
  const columnKeys: string[] = [];

  visibleColumns.forEach(colId => {
    switch (colId) {
      case 'timestamp':
        headers.push('Timestamp');
        columnKeys.push('timestamp');
        break;
      case 'assetCode':
        headers.push('Asset Code');
        columnKeys.push('assetCode');
        break;
      case 'assetName':
        headers.push('Description');
        columnKeys.push('assetName');
        break;
      case 'user':
        headers.push('User');
        columnKeys.push('user');
        break;
      case 'actionType':
        headers.push('Action');
        columnKeys.push('actionType');
        break;
      case 'field':
        headers.push('Field');
        columnKeys.push('field');
        break;
      case 'changes':
        headers.push('Changes');
        columnKeys.push('changes');
        break;
    }
  });

  // Prepare table data
  const tableData = data.map(log => {
    return columnKeys.map(key => {
      switch (key) {
        case 'timestamp':
          return format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss');
        case 'assetCode':
          return log.assetCode || '-';
        case 'assetName':
          return log.assetName || '-';
        case 'user':
          return log.user || '-';
        case 'actionType':
          return formatActionType(log.actionType);
        case 'field':
          return formatFieldName(log.field);
        case 'changes':
          if (log.oldValue && log.newValue) {
            return `${log.oldValue} → ${log.newValue}`;
          }
          return '-';
        default:
          return '-';
      }
    });
  });

  // Add table
  autoTable(doc, {
    head: [headers],
    body: tableData,
    startY: yPosition,
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [66, 66, 66],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'left',
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      // Add page numbers
      const pageCount = doc.getNumberOfPages();
      const pageSize = doc.internal.pageSize;
      const pageHeight = pageSize.height || pageSize.getHeight();
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        pageSize.width / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    },
  });

  // Add summary at the end
  const finalY = (doc as any).lastAutoTable.finalY || yPosition + 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`Total Records: ${data.length}`, 14, finalY + 10);

  return doc;
}

export function downloadPDF(doc: jsPDF, filename: string): void {
  doc.save(filename);
}

export function getPDFBlob(doc: jsPDF): Blob {
  return doc.output('blob');
}

export function getPDFBase64(doc: jsPDF): string {
  return doc.output('dataurlstring');
}
